import { useEffect, useMemo, useState } from "react";
import TopNav from "../components/TopNav";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  OutlinedInput,
  Switch,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ImportContacts, Search } from "@mui/icons-material";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import { APICustomer, getAllCustomers, saveCustomer } from "../api/customers";
import axios from "axios";
import pageStyle from "./ContactForm.module.css";

type Lead = {
  id: number | string;
  firstname: string;
  lastname: string;
  address: string;
  city: string;
  zipcode: string;
  state: string;
  phone: string;
  email: string;
  voucher: string;
  message: string;
  timestamp?: string;
  sms_optin?: number | boolean;
};

// "+17165533318" -> "(716) 553-3318"; anything else stays as typed.
const formatPhone = (s?: string) => {
  const d = digits(s);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : s || "";
};

const isEmergency = (r: Lead) => /Timing: EMERGENCY/.test(r.message || "");

const digits = (s?: string) => (s || "").replace(/\D/g, "").slice(-10);

function ContactFormImportPage() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const [selected, setSelected] = useState<readonly GridRowId[]>([]);
  const [rows, setRows] = useState<Lead[]>([]);
  const [customers, setCustomers] = useState<APICustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [hideExisting, setHideExisting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get("https://dashboard.lifetreewny.com/api/dashboard/formData/", {
        headers: {
          Authorization: "Bearer " + token,
        },
      })
      // Newest first.
      .then((d) =>
        setRows(
          [...(d.data as Lead[])].sort((a, b) => Number(b.id) - Number(a.id))
        )
      )
      .catch(() =>
        setError(
          "Couldn't load contact form submissions from the dashboard. Check the connection and try again."
        )
      )
      .finally(() => setLoading(false));
    getAllCustomers().then(setCustomers);
  }, []);

  // Leads whose email or phone already belongs to a customer.
  const existing = useMemo(() => {
    const emails = new Set(
      customers.map((c) => c.email.trim().toLowerCase()).filter(Boolean)
    );
    const phones = new Set(customers.map((c) => digits(c.phone)).filter(Boolean));
    return new Set(
      rows
        .filter(
          (r) =>
            (r.email && emails.has(r.email.trim().toLowerCase())) ||
            (digits(r.phone) && phones.has(digits(r.phone)))
        )
        .map((r) => r.id)
    );
  }, [rows, customers]);

  const visibleRows = useMemo(() => {
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter(
      (r) =>
        (!hideExisting || !existing.has(r.id)) &&
        terms.every((t) =>
          [r.firstname, r.lastname, r.phone, r.email, r.address, r.city, r.zipcode, r.voucher, r.message]
            .join(" ")
            .toLowerCase()
            .includes(t)
        )
    );
  }, [rows, search, hideExisting, existing]);

  const toggle = (id: GridRowId) =>
    setSelected(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id]
    );

  const handleImport = async () => {
    setImporting(true);
    let count = 0;
    for (const sid of selected) {
      const row = rows.find((v) => v.id === sid);
      if (row === undefined) {
        continue;
      }
      const customer: APICustomer = {
        firstName: row.firstname || "",
        lastName: row.lastname || "",
        addressLine1: row.address || "",
        addressLine2: "",
        city: row.city || "",
        zip: row.zipcode || "",
        state: row.state || "NY",
        phone: formatPhone(row.phone),
        phoneExt: "",
        email: row.email || "",
        notes: [
          row.message,
          row.voucher && `Voucher: ${row.voucher}`,
          row.sms_optin &&
            !/Agreed to text messages: yes/.test(row.message || "") &&
            `Agreed to text messages on the website form${row.timestamp ? ` (${row.timestamp})` : ""}.`,
        ]
          .filter(Boolean)
          .join("\n"),
      };
      await saveCustomer(customer);
      count++;
    }
    setImporting(false);
    window.alert(`Imported ${count} customer${count === 1 ? "" : "s"}.`);
    navigate("/customers");
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 200,
      valueGetter: (_v, r: Lead) => `${r.firstname} ${r.lastname}`.trim(),
      // Tags sit under the name so they're visible without scrolling.
      renderCell: (p) => (
        <div style={{ padding: "8px 0" }}>
          <div>{p.value}</div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
            {isEmergency(p.row) && (
              <Chip size="small" color="error" label="Emergency" />
            )}
            {existing.has(p.row.id) && (
              <Chip size="small" label="Already a customer" />
            )}
            {!!p.row.sms_optin && (
              <Chip size="small" color="success" label="OK to text" />
            )}
          </div>
        </div>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      minWidth: 140,
      valueGetter: (v: string) => formatPhone(v),
    },
    { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
      valueGetter: (_v, r: Lead) =>
        [r.address, r.city, `${r.state || ""} ${r.zipcode || ""}`.trim()]
          .filter(Boolean)
          .join(", "),
    },
    { field: "voucher", headerName: "Voucher", minWidth: 110 },
    {
      field: "message",
      headerName: "Message",
      flex: 1.5,
      minWidth: 260,
      renderCell: (p) => (
        <div style={{ whiteSpace: "pre-line", padding: "8px 0" }}>{p.value}</div>
      ),
    },
  ];

  return (
    <div className="page">
      <TopNav pageTitle="Contact Form" />
      <main className={pageStyle.main}>
        <div className={pageStyle.header}>
          <div>
            <Typography variant="h6">Website leads</Typography>
            <Typography variant="body2" color="text.secondary">
              Select the people to add as customers.
            </Typography>
          </div>
          <Button
            onClick={handleImport}
            variant="contained"
            startIcon={<ImportContacts />}
            disabled={selected.length === 0 || importing}
          >
            {importing
              ? "Importing…"
              : `Import ${selected.length || ""} Selected`.replace("  ", " ")}
          </Button>
        </div>

        {!loading && !error && (
          <div className={pageStyle.filters}>
            <OutlinedInput
              size="small"
              placeholder="Search name, phone, email, address or message"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              }
              className={pageStyle.search}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={hideExisting}
                  onChange={(e) => setHideExisting(e.target.checked)}
                />
              }
              label="Hide existing customers"
            />
            <Typography variant="body2" color="text.secondary">
              {visibleRows.length} of {rows.length}
            </Typography>
          </div>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {loading && (
          <div className={pageStyle.loading}>
            <CircularProgress />
          </div>
        )}

        {!loading &&
          !error &&
          (isDesktop ? (
            <div className={pageStyle.gridBox}>
              <DataGrid
                columns={columns}
                rows={visibleRows}
                pagination
                checkboxSelection
                rowSelectionModel={[...selected]}
                onRowSelectionModelChange={setSelected}
                getRowHeight={() => "auto"}
                sx={{ background: "var(--surface)" }}
              />
            </div>
          ) : (
            <div className={pageStyle.cards}>
              {visibleRows.length === 0 && (
                <Typography color="text.secondary">
                  {rows.length ? "No leads match." : "No submissions."}
                </Typography>
              )}
              {visibleRows.map((r) => (
                <label key={r.id} className={pageStyle.card}>
                  <Checkbox
                    checked={selected.includes(r.id)}
                    onChange={() => toggle(r.id)}
                  />
                  <div className={pageStyle.cardBody}>
                    <div className={pageStyle.cardTitle}>
                      <strong>
                        {`${r.firstname} ${r.lastname}`.trim() || "No name"}
                      </strong>
                      {isEmergency(r) && (
                        <Chip size="small" color="error" label="Emergency" />
                      )}
                      {existing.has(r.id) && (
                        <Chip size="small" label="Already a customer" />
                      )}
                      {!!r.sms_optin && (
                        <Chip size="small" color="success" label="OK to text" />
                      )}
                    </div>
                    {r.phone && <div>{formatPhone(r.phone)}</div>}
                    {r.email && <div>{r.email}</div>}
                    {r.address && (
                      <div className={pageStyle.muted}>
                        {[r.address, r.city].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {r.voucher && (
                      <div className={pageStyle.muted}>Voucher: {r.voucher}</div>
                    )}
                    {r.message && <p className={pageStyle.message}>{r.message}</p>}
                  </div>
                </label>
              ))}
            </div>
          ))}
      </main>
    </div>
  );
}

export default ContactFormImportPage;
